import {
    AccountGet,
    PeriodGet,
    Ulid,
    VatClassGet,
} from "@kase/my-marathon-communication";
import { computed, signal } from "@preact/signals";
import AppTime from "~/core/AppTime";
import { dateFromApi } from "~/utils";
import { ApiSliceParams } from "../interfaces";
import { createListController, getIdWithCompany } from "../utils";
import { Account, Period, VatClass } from "./interfaces";

export const makeVatClass = ({ connection, company }: ApiSliceParams) => {
    const {
        items: periods,
        listen: listenPeriods,
        whenLoaded: whenPeriodsLoaded,
    } = createListController<PeriodGet, Period, string>({
        connection,
        list: (con, company) => con.company.period.items({ company }),
        sort: (a, b) => b.end - a.end,
        getId: (period, company) => getIdWithCompany(period.id, company),
        getKey: (company) => company,
        name: "period",
    });
    const {
        items: vatClasses,
        listen: listenVatClasses,
        loading: loadingVatClasses,
    } = createListController<
        VatClassGet,
        VatClass,
        { year: number; company: string }
    >({
        connection,
        list: (con, { company, year }) => con.vatClass.items({ company, year }),
        view: (item, { company }) => ({
            ...item,
            company,
            id: getIdWithCompany(item.id, company),
            _id: item.id,
        }),
        getId: (vatClass, { company }) =>
            getIdWithCompany(vatClass.id, company),
        getKey: ({ year, company }) => `${company}-${year}`,
        name: "vatClass",
    });
    const { items: accounts, listen: listenAccounts } = createListController<
        AccountGet,
        Account,
        { year: number; company: string }
    >({
        connection,
        list: (con, { company, year }) => con.account.items({ company, year }),
        getId: (account, { company }) => getIdWithCompany(account.id, company),
        getKey: ({ year, company }) => `${company}-${year}`,
        name: "account",
    });
    const bookKeepYear = signal(0);
    const finished = signal(false);
    const loading = computed(() => {
        const f = finished.value;
        const y = bookKeepYear.value;

        if (f) {
            return false;
        }

        if (!y) {
            return true;
        }

        return loadingVatClasses({ company: company.value, year: y }).value;
    });

    const listenCompany = async (company: string) => {
        listenPeriods(company);

        await whenPeriodsLoaded(company);

        const today = new Date();
        const period =
            periods
                .peek()
                .find(
                    (p) =>
                        dateFromApi(p.start) <= today &&
                        dateFromApi(p.end) >= today,
                ) ?? periods.peek()[0];

        finished.value = true;

        if (!period) {
            return;
        }

        bookKeepYear.value = period.bookkeep_year;

        const cancelVatClasses = listenVatClasses({
            company,
            year: period.bookkeep_year,
        });
        const cancelAccounts = listenAccounts({
            company,
            year: period.bookkeep_year,
        });

        AppTime.on("year", () => {
            cancelAccounts();
            cancelVatClasses();
            listenCompany(company);
        });
    };

    const listen = () => {
        listenCompany(company.value);
    };

    const getAccount = (accountCode: string | null | undefined) => {
        if (!accountCode) {
            return null;
        }

        return accounts.value.find((a) => a.code === accountCode) ?? null;
    };

    const getAccountById = (id: string | null | undefined | Readonly<Ulid>) => {
        if (!id) {
            return null;
        }

        const finalId = typeof id === "string" ? id : id.toString();

        return accounts.value.find((a) => a._id.toString() === finalId) ?? null;
    };

    const get = (
        id: string | Readonly<Ulid> | null | undefined,
        cmp = company.value,
    ) => {
        if (!id) {
            return null;
        }

        const key = getIdWithCompany(id, cmp);

        return vatClasses.value.find((s) => s.id === key) ?? null;
    };

    return {
        vatClasses,
        loading,
        listen,
        listenCompany,
        get,
        getAccount,
        getAccountById,
    };
};
